import { TemplateRuleRow, SelectedFieldOptionType, OptionType } from "./RulesModal.types";
import { makeCygnusApiCall } from "../../../api/api";


/**
 * Validates a rule form.
 */
export const validateRuleForm = (
  ruleName: string,
  selectedField: SelectedFieldOptionType,
  operator: OptionType,
  conditionValue: string,
  existingRows: TemplateRuleRow[],
  editingRow: TemplateRuleRow | null,
  selectedFieldsList: OptionType[]
): { valid: boolean; errors: Record<string, string> } => {
  const errors: Record<string, string> = {};
  const emptyMsg = "Field cannot be empty";
  
  if (!ruleName?.trim()) errors.ruleName = emptyMsg;
  if (!selectedField || !selectedField.label?.trim()) errors.selectedField = emptyMsg;
  if (!operator || !operator.label?.trim()) errors.operator = emptyMsg;
  if (!conditionValue?.trim()) errors.conditionValue = emptyMsg;

  if (
    ruleName?.trim() &&
    selectedField?.label?.trim() &&
    operator?.label?.trim() &&
    conditionValue?.trim()
  ) {
    const lowerCaseRuleName = ruleName.toLowerCase();

    const isSelectedFieldNameMatch = selectedFieldsList
      .map(f => f.label.toLowerCase())
      .includes(lowerCaseRuleName)

    const isRuleConditionNameMatch = existingRows
      .filter(row => row.Active === "Y")
      .filter(row => row !== editingRow)
      .filter(row => row.SelectedFieldLabel === selectedField.label)
      .some(row => row.ConditionValue.toLowerCase() === conditionValue.toLowerCase());

    if (isSelectedFieldNameMatch) {
      errors.ruleName = "The rule name must be distinct from the selected field names.";
    }
    if (isRuleConditionNameMatch) {
      errors.conditionValue = "The condition value already exists.";
    }
  }

  return { valid: Object.keys(errors).length === 0, errors };
};

/**
 * Creates a row object for saving.
 */
export const buildNewRow = (
  ruleName: string,
  selectedField: SelectedFieldOptionType,
  operator: OptionType,
  conditionValue: string,
  editingRow: TemplateRuleRow | null,
  fieldCode: string,
  conditionValueType: 'LITERAL' | 'FIELD' = 'LITERAL'
): TemplateRuleRow => ({
  GroupId: editingRow?.GroupId || "",
  RuleId: editingRow?.RuleId || 0,
  RuleDetailId: editingRow?.RuleDetailId || 0,
  FieldCode: fieldCode,
  RuleName: ruleName || "",
  SelectedFieldLabel: selectedField?.label || "",
  SelectedFieldValue: selectedField?.value || "",
  Operator: operator?.label || "",
  ConditionValue: conditionValue,
  ConditionValueType: conditionValueType,
  Active: "Y",
});

/**
 * Fetches firm rules.
 * @param ruleId - Optional RuleId filter
 * @param ruleName - Optional RuleName filter
 * @returns Rules with details
 */
export const fetchFirmRules = async (
  ruleId?: number | string,
  ruleName?: string
): Promise<any[]> => {
  let url = `/firm_setup/firm_rules`;
  if (ruleId) url += `?RuleId=${ruleId}`;
  else if (ruleName) url += `?RuleName=${ruleName}`;

  const response = await makeCygnusApiCall({
    type: "GET",
    url,
    show_message: false,
    customized_success_message: "Fetched firm rules successfully.",
    customized_failure_message: "Failed to fetch firm rules.",
    customized_error_message: "Error occurred while fetching firm rules.",
    isblob: false,
    forMDM: true,
  });

  return response?.data ?? [];
};

/**
 * Fetches firm rule details.
 * @param ruleId - Optional RuleId filter
 * @param ruleName - Optional RuleName filter
 * @returns Rules with details
 */
export const fetchFirmRuleDetails = async (
  ruleId?: number | string
): Promise<any[]> => {
  let url = `/firm_setup/firm_rule_details`;
  if (ruleId) url += `?RuleId=${ruleId}`;

  const response = await makeCygnusApiCall({
    type: "GET",
    url,
    show_message: false,
    customized_success_message: "Fetched firm rule details successfully.",
    customized_failure_message: "Failed to fetch firm rule details.",
    customized_error_message: "Error occurred while fetching firm rule details.",
    isblob: false,
    forMDM: true,
  });

  return response?.data ?? [];
};

/**
 * Fetches the firm's "Add approver by Rule" privilege catalog -- every
 * tblFirmUserRules scope key tagged with a DisplayName (e.g. COI_REG),
 * DB-driven instead of the previous hardcoded frontend list.
 * @returns Array of {value, label} options for the rule-approver dropdown.
 */
export const fetchRuleApproverPrivileges = async (): Promise<{ value: string; label: string }[]> => {
  const response = await makeCygnusApiCall({
    type: "GET",
    url: `/firm_setup/rule_approver_privileges`,
    show_message: false,
    customized_success_message: "Fetched approver privileges successfully.",
    customized_failure_message: "Failed to fetch approver privileges.",
    customized_error_message: "Error occurred while fetching approver privileges.",
    isblob: false,
    forMDM: true,
  });

  return response?.data ?? [];
};

/**
 * Fetches firm approvers (Client or EL).
 * @param ruleId - RuleId for which to fetch approvers
 * @param ruleType - "Client" or "EL"
 * @returns Approvers data
 */
export const fetchFirmApprovalDetails = async (
  ruleId: number | string,
  ruleType: "Client" | "EL"
): Promise<any[]> => {
  const response = await makeCygnusApiCall({
    type: "GET",
    url: `/firm_setup/firm_approval?RuleId=${ruleId}&RuleType=${ruleType}`,
    show_message: false,
    customized_success_message: "Fetched firm approvers successfully.",
    customized_failure_message: "Failed to fetch firm approvers.",
    customized_error_message: "Error occurred while fetching firm approvers.",
    isblob: false,
    forMDM: true,
  });

  return response?.data ?? [];
};

/**
 * Saves or updates firm approval config (rule + approvers).
 * If RuleId exists, it updates. Otherwise creates a new Rule then saves approvers.
 *
 * @param configs - Array of rule + approver config objects
 * @returns boolean success
 */
export const saveFirmApprovalDetails = async (configs: any[]): Promise<boolean> => {
  const response = await makeCygnusApiCall({
    type: "POST",
    url: "/firm_setup/firm_approval",
    payload: configs,
    show_message: true,
    customized_success_message: "Firm approval configuration saved successfully.",
    customized_failure_message: "Failed to save firm approval configuration.",
    customized_error_message: "Error occurred while saving firm approval configuration.",
    isblob: false,
    forMDM: true,
    default_message: true,
  });

  return response.success;
};

/**
 * Inserts or updates firm rules.
 *
 * @param configs - Array of rules
 * @returns boolean success
 */
export const saveFirmRules = async (configs: any[]): Promise<boolean> => {
  const response = await makeCygnusApiCall({
    type: "POST",
    url: "/firm_setup/firm_rules",
    payload: configs,
    show_message: false,
    customized_success_message: "Firm rules saved successfully.",
    customized_failure_message: "Failed to save firm rules configuration.",
    customized_error_message: "Error occurred while saving rules.",
    isblob: false,
    forMDM: true,
  });

  return response.success;
};
